import { useMemo, useState } from "react";

import {
  Modal,
  Paper,
  TextField,
  Grid,
  Button,
  Typography,
  FormControl,
  Autocomplete,
} from "@mui/material";

import { Group } from "types";
import { useSession } from "providers/SessionProvider";

type Props = {
  open: boolean;
  handleOpen: () => void;
  onCreate: (newGroup: Group) => void;
};

export default function AddGroupModal({
  open = true,
  handleOpen,
  onCreate,
}: Props) {
  const defaultValues: Omit<Group, "id"> = {
    code: "",
    description: "",
    client_id: null,
    client: null,
  };

  const [data, setData] = useState<Omit<Group, "id">>(defaultValues);
  const { clients } = useSession();
  const [selectedClient, setSelectedClient] = useState<{
    id: number;
    label: string;
  } | null>(null);

  const handleChange = <K extends keyof Omit<Group, "id">>(
    name: K,
    value: Omit<Group, "id">[K],
  ) => {
    setData({
      ...data,
      [name]: value,
    });
  };

  const onSubmit = (newGroup: Omit<Group, "id">) => {
    onCreate(newGroup as Group);
    setData(defaultValues);
    setSelectedClient(null);
    handleOpen();
  };

  const handleClose = () => {
    setData(defaultValues);
    setSelectedClient(null);
    handleOpen();
  };

  const options = useMemo(() => {
    const clientList = clients.map((client) => ({
      id: client.id,
      label: `${client.code} - ${client.razon_social}`,
    }));
    return clientList;
  }, [clients]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { sm: "90%", md: "30rem" },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <form
          onSubmit={() => {
            onSubmit(data);
          }}
        >
          <Grid container spacing={2}>
            <Typography variant="h5" gutterBottom>
              Nuevo grupo de artículos
            </Typography>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required margin="none">
                <TextField
                  label="Código"
                  type="text"
                  fullWidth
                  onChange={(e) => handleChange("code", e.target.value)}
                  value={data.code}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required margin="none">
                <TextField
                  label="Descripción"
                  type="text"
                  fullWidth
                  onChange={(e) => handleChange("description", e.target.value)}
                  value={data.description}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="client"
                  autoComplete
                  value={selectedClient}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  options={options}
                  onChange={(
                    e,
                    value: {
                      id: number;
                      label: string;
                    } | null,
                  ) => {
                    handleChange("client_id", value?.id || null);
                    setSelectedClient(value || null);
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} required label="Cliente" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
              <Button type="button" variant="outlined" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!data.code || !data.description || !data.client_id}
              >
                Guardar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Modal>
  );
}
